import {
  addDays,
  fmtDate,
  plural,
  todayStr,
} from "./format";

/** Derived reads over the dashboard state — ported 1:1 from the v1.1 (Blue) prototype. */

export const jobsByStatus = (state, status) =>
  (state.jobs || []).filter((j) => j.status === status);

export const findJob = (state, id) => (state.jobs || []).find((j) => j.id === id);

/** True when the MC marked at least one of the job's client texts as reviewed. */
export const hasReviewedTexts = (job) =>
  !!job && Array.isArray(job.reviewedTexts) && job.reviewedTexts.length > 0;

/** Unanswered-text items whose messageId the MC has not marked reviewed. */
export const unreviewedTexts = (state, texts) =>
  (texts || []).filter((t) => {
    const job = findJob(state || {}, t.jobId);
    const reviewed = job && Array.isArray(job.reviewedTexts) ? job.reviewedTexts : [];
    return t.messageId == null || !reviewed.map(String).includes(String(t.messageId));
  });

/**
 * Long distance jobs the warehouse received (Into Trailer + Roadway Receiving
 * reports) whose Zoho stage was never moved to SIT Local: only the stage puts a
 * job in SIT Local, so the LD team has to fix it (Asana 1219432727729888).
 * Gone by itself once the stage is updated.
 */
export const stageMissingItems = (state) =>
  ((state && state.jobs) || [])
    .filter((j) => j.sitLocalStageMissing)
    .map((j) => ({
      id: "stage-" + j.id,
      kind: "stage",
      channel: "internal",
      text: "Received at hub, Zoho stage not updated to SIT Local. Ask the LD team to update it.",
      date: j.receivedAtHubAt,
      jobId: j.id,
    }));

/**
 * The sidebar count on Needs Attention: calls still to return and texts still
 * unanswered (the past week when the backend sends it, else the 24 h feed),
 * overdue reminders and SIT Local stage flags.
 */
export const needsAttentionCount = (state, today = todayStr()) => {
  const feed = (state && (state.unansweredOpen || state.unanswered24h)) || {};
  const overdue = ((state && state.reminders) || []).filter(
    (r) => !r.completed && r.date && r.date < today,
  ).length;
  return (feed.calls || []).length + unreviewedTexts(state, feed.texts).length + overdue + stageMissingItems(state).length;
};

export const weekStats = (jobsList) => {
  const today = todayStr();
  const lastWeekStart = addDays(today, -7);
  const nextWeekEnd = addDays(today, 7);
  const lastWeek = jobsList.filter(
    (j) => j.moveDate >= lastWeekStart && j.moveDate < today,
  ).length;
  const nextWeek = jobsList.filter(
    (j) => j.moveDate >= today && j.moveDate <= nextWeekEnd,
  ).length;
  return { lastWeek, nextWeek };
};

export const suggestCrewSize = (cubicFeetStr) => {
  const num = parseInt(String(cubicFeetStr || "").replace(/[^0-9]/g, ""), 10);
  if (!num) return null;
  if (num <= 500) return "2–3 movers, 1 truck";
  if (num <= 900) return "3–4 movers, 1 truck";
  if (num <= 1400) return "4–5 movers, 1 truck (or 26ft)";
  return "5+ movers, 2 trucks likely";
};

export const RISK_NOTE_KEYS = {
  "Scope change": "scopeChangeNote",
  "Additional service needed": "additionalServiceNote",
  "Sales/client misalignment": "salesClientNote",
  "Move date change": "moveDateChangeNote",
};

export const LD_RISK_NOTE_KEYS = {
  "Scope change — sales required": "scopeChangeNoteLD",
  "Service required vs quoted": "serviceVsQuotedNoteLD",
  "Logistical misalignment — move dates, restrictions":
    "logisticalMisalignmentNoteLD",
  "Delivery window misalignment": "deliveryWindowMisalignmentNoteLD",
};

/** One address's COI line: a private house needs none; a missing flag counts as required. */
const coiText = (required, completed) => {
  if (required === false) return "Not required (private house)";
  return completed ? "Confirmed" : "COI REQUIRED — NOT CONFIRMED";
};

export const buildDispatchBriefText = (j) => {
  const n = (k) => (j.s2Notes && j.s2Notes[k]) || "";
  const crewEst = suggestCrewSize(j.totalCubicFeet);
  const lines = [];
  lines.push("DISPATCH & TIMING BRIEF — " + j.zohoId + " · " + j.clientName);
  lines.push(
    "Move date: " + fmtDate(j.moveDate) + "   Start time: " + (j.startTime || "—"),
  );
  lines.push("Total cubic feet: " + (j.totalCubicFeet || "—"));
  if (crewEst)
    lines.push("Suggested crew size (estimate — confirm with dispatch): " + crewEst);
  lines.push("");
  lines.push("PICKUP");
  lines.push("Stairs: " + (j.stairsAtPickup ? n("stairsFlights") || "Yes" : "No"));
  lines.push(
    "Elevator: " + (j.elevatorAtPickup ? j.elevatorRestriction || "Yes" : "No"),
  );
  lines.push("COI: " + coiText(j.coiRequiredPickup, j.coiCompletedPickup));
  lines.push("Long carry: " + (n("longCarryPickup") || "—"));
  if (n("pickupMasonite")) lines.push("Masonite required: " + n("pickupMasonite"));
  lines.push("");
  lines.push("DELIVERY");
  lines.push(
    "Stairs: " + (j.stairsAtDelivery ? n("stairsFlightsDelivery") || "Yes" : "No"),
  );
  lines.push(
    "Elevator: " +
      (j.elevatorAtDelivery ? j.elevatorRestrictionDelivery || "Yes" : "No"),
  );
  lines.push("COI: " + coiText(j.coiRequiredDelivery, j.coiCompletedDelivery));
  lines.push("Long carry: " + (n("longCarryDelivery") || "—"));
  if (n("deliveryMasonite")) lines.push("Masonite required: " + n("deliveryMasonite"));
  lines.push("");
  lines.push("SPECIAL SERVICES CONFIRMED");
  lines.push("Carpenter: " + (j.carpenterIncluded ? "Included" : "Not included"));
  lines.push("Assembly: " + (j.assemblyIncluded ? "Included" : "Not included"));
  lines.push("Disassembly: " + (j.disassemblyIncluded ? "Included" : "Not included"));
  lines.push("TV service: " + (j.tvServiceIncluded ? "Included" : "Not included"));
  lines.push("Disposal service: " + (j.disposalIncluded ? "Included" : "Not included"));
  if (n("specialServicesNotes"))
    lines.push("Special service notes: " + n("specialServicesNotes"));
  lines.push("");
  lines.push("FLAGGED DURING CONFIRMATION CALL");
  if (j.riskFlags && j.riskFlags.length) {
    j.riskFlags.forEach((r) => {
      const note = n(RISK_NOTE_KEYS[r]);
      lines.push("- " + r + (note ? ": " + note : ""));
    });
  } else {
    lines.push("All clear — no risks flagged.");
  }
  lines.push("");
  lines.push("NOTES FOR THE CREW");
  lines.push(n("confCrewNotes") || "No specific crew notes captured yet.");
  return lines.join("\n");
};

export const computeDailySummary = (state) => {
  const today = todayStr();
  const isToday = (d) => (d || "").slice(0, 10) === today;

  const calledConfirmed = new Set();
  const introCompleted = new Set();
  let pdfGenerated = 0;
  let postMoveClosed = 0;

  (state.jobs || []).forEach((j) => {
    const todaysNotes = (j.notes || []).filter((n) => isToday(n.date));
    if (todaysNotes.some((n) => n.text === "Job confirmed.")) calledConfirmed.add(j.id);
    if (
      todaysNotes.some(
        (n) =>
          n.text.includes("Introduction call marked done") ||
          n.text.includes("Introduction completed only"),
      )
    )
      introCompleted.add(j.id);
    if (todaysNotes.some((n) => n.text.includes("Confirmation PDF"))) pdfGenerated++;
    if (todaysNotes.some((n) => n.text.includes("Post-move follow-up closed")))
      postMoveClosed++;
  });

  const calledNotConfirmed = new Set();
  (state.notifExternal || [])
    .filter((n) => n.channel === "outbound-call" && isToday(n.date))
    .forEach((n) => {
      const job = findJob(state, n.jobId);
      if (job && !calledConfirmed.has(job.id)) calledNotConfirmed.add(job.id);
    });

  const countExt = (channel) =>
    (state.notifExternal || []).filter(
      (n) => n.channel === channel && isToday(n.date),
    ).length;

  const textsSent = countExt("text");
  const emailsSent = countExt("email");
  const outboundCalls = countExt("outbound-call");
  const inboundCalls = countExt("inbound-call");
  const missedCalls = countExt("missed-call");
  const escalationsRaised = (state.escalations || []).filter((e) =>
    isToday(e.createdAt),
  ).length;
  const escalationsResolved = (state.escalations || []).filter(
    (e) => e.resolvedAt && isToday(e.resolvedAt),
  ).length;
  const remindersLogged = (state.notifInternal || []).filter(
    (n) => n.type === "reminder" && isToday(n.date),
  ).length;
  const notesLogged = (state.notifInternal || []).filter(
    (n) => n.type === "note" && isToday(n.date),
  ).length;
  const inProgressCount = jobsByStatus(state, "in-progress").length;

  const paragraph =
    `On ${fmtDate(today)}, the MC handled ${outboundCalls} outbound and ${inboundCalls} inbound ${plural(outboundCalls + inboundCalls, "call")} across the active pipeline. ` +
    `${calledNotConfirmed.size} ${plural(calledNotConfirmed.size, "job was", "jobs were")} called but could not be confirmed, while ${calledConfirmed.size} ${plural(calledConfirmed.size, "job was", "jobs were")} successfully confirmed. ` +
    `${introCompleted.size} introduction ${plural(introCompleted.size, "call was", "calls were")} completed, and ${inProgressCount} ${plural(inProgressCount, "move is", "moves are")} currently in progress today. ` +
    `${escalationsRaised} ${plural(escalationsRaised, "escalation was", "escalations were")} raised (${escalationsResolved} resolved), with ${textsSent} ${plural(textsSent, "text")} and ${emailsSent} ${plural(emailsSent, "email")} sent, plus ${notesLogged} ${plural(notesLogged, "note")} and ${remindersLogged} new ${plural(remindersLogged, "reminder")} logged.`;

  return {
    calledNotConfirmed: calledNotConfirmed.size,
    calledConfirmed: calledConfirmed.size,
    introCompleted: introCompleted.size,
    inProgressCount,
    outboundCalls,
    inboundCalls,
    missedCalls,
    textsSent,
    emailsSent,
    escalationsRaised,
    escalationsResolved,
    remindersLogged,
    notesLogged,
    pdfGenerated,
    postMoveClosed,
    paragraph,
  };
};

export const pctOf = (num, den) =>
  den > 0 ? Math.min(100, Math.round((num / den) * 100)) : 100;

export const computeProgressStats = (state) => {
  const s = computeDailySummary(state);
  const today = todayStr();

  const confirmRemaining =
    jobsByStatus(state, "daily-assign").length +
    jobsByStatus(state, "confirmed-update").length +
    jobsByStatus(state, "ready-to-confirm").length +
    jobsByStatus(state, "weekly-assign").length +
    jobsByStatus(state, "ld-to-confirm").length;
  const confirmTotal = confirmRemaining + s.calledConfirmed;

  const introRemaining = (state.jobs || []).filter((j) => !j.introCallDone).length;
  const introTotal = introRemaining + s.introCompleted;

  const movesToday = (state.jobs || []).filter((j) => j.moveDate === today).length;
  const escTotal = s.escalationsRaised;

  const priorityItems = []
    .concat(
      (state.escalations || [])
        .filter((e) => e.status === "open")
        .map((e) => ({ ic: "alert", color: "red", text: e.note, meta: e.jobLabel })),
    )
    .concat(
      (state.reminders || [])
        .filter((r) => r.date === today)
        .map((r) => ({ ic: "bell", color: "amber", text: r.text, meta: r.jobLabel })),
    )
    .slice(0, 6);

  return {
    s,
    confirmNum: s.calledConfirmed,
    confirmDen: confirmTotal,
    introNum: s.introCompleted,
    introDen: introTotal,
    moveNum: s.postMoveClosed,
    moveDen: movesToday,
    escNum: s.escalationsResolved,
    escDen: escTotal,
    priorityItems,
  };
};

/**
 * The job's shuttle: included when Zoho has a shuttle service line (the one the
 * Introduction call lists under special services) or the MC said so; the Zoho
 * rate is that line's price times its quantity, else null (Asana 1220099429071097).
 */
export const shuttleOf = (job) => {
  const lines = ((job && job.additionalServiceItems) || []).filter((i) => /shuttle/i.test((i && i.service) || ""));
  const priced = lines.filter((i) => Number(i.price) > 0);
  const zohoRate = priced.length
    ? priced.reduce((sum, i) => sum + Number(i.price) * Math.max(1, Number(i.quantity) || 1), 0)
    : null;
  return {
    included: lines.length > 0 || /^yes/i.test((job && job.shuttleIncluded) || ""),
    fromZoho: lines.length > 0,
    zohoRate,
  };
};
